import {Audio} from 'interfaces';

import {YouTubeSignInResponse} from './youtubeActions';

const YOUTUBE_AUTH_STORAGE_KEY = 'wellnite_youtube_auth';
const PEOPLE_BLOGS_CATEGORY = '22';

export const saveYouTubeAuth = (authData: YouTubeSignInResponse): void => {
  try {
    const dataWithTimestamp = {
      ...authData,
      tokenTimestamp: Date.now(),
    };
    localStorage.setItem(
      YOUTUBE_AUTH_STORAGE_KEY,
      JSON.stringify(dataWithTimestamp),
    );
  } catch (error) {
    console.error('Failed to save YouTube auth data to localStorage:', error);
  }
};

export const loadYouTubeAuth = (): YouTubeSignInResponse | null => {
  try {
    const storedData = localStorage.getItem(YOUTUBE_AUTH_STORAGE_KEY);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.error('Failed to load YouTube auth data from localStorage:', error);
  }
  return null;
};

export const clearYouTubeAuth = (): void => {
  try {
    localStorage.removeItem(YOUTUBE_AUTH_STORAGE_KEY);
  } catch (error) {
    console.error(
      'Failed to clear YouTube auth data from localStorage:',
      error,
    );
  }
};

export const doUploadToYoutube = async (audio: Audio): Promise<any> => {
  try {
    const authData = loadYouTubeAuth();
    if (!authData || !authData.accessToken) {
      return Promise.reject(
        'YouTube authentication required. Please sign in first.',
      );
    }

    // Wait for gapi to be initialized
    if (!(window as any).gapi || !(window as any).gapiReady) {
      await new Promise<void>((resolve, reject) => {
        const checkGapiLoaded = setInterval(() => {
          if ((window as any).gapiReady && (window as any).gapi) {
            clearInterval(checkGapiLoaded);
            clearTimeout(timeout);
            resolve();
          }
        }, 100);

        const timeout = setTimeout(() => {
          clearInterval(checkGapiLoaded);
          reject(new Error('Google API client not loaded'));
        }, 8000);
      });
    }

    const gapi = (window as any).gapi;

    // Make sure the client is fully initialized
    if (!gapi.client) {
      await new Promise<void>(resolve => {
        gapi.load('client', () => {
          resolve();
        });
      });
    }

    gapi.client.setApiKey(process.env.REACT_APP_YOUTUBE_API_KEY);
    gapi.client.setToken({access_token: authData.accessToken});

    if (!gapi.client.youtube) {
      await new Promise<void>(resolve => {
        gapi.client.load('youtube', 'v3').then(() => resolve());
      });
    }

    const missingChannelError = {
      error: {
        message: 'User is not connected to a YouTube channel',
        errors: [{reason: 'youtubeSignupRequired'}],
      },
    };

    // First, make a simple request to test if token is valid
    try {
      const channelResponse = await gapi.client.youtube.channels.list({
        part: 'snippet',
        mine: true,
      });

      if (
        !channelResponse.result.items ||
        channelResponse.result.items.length === 0
      ) {
        return Promise.reject(missingChannelError);
      }
    } catch (error: any) {
      if (
        error?.result?.error?.errors?.some(
          (err: any) =>
            err.reason === 'youtubeSignupRequired' ||
            err.message?.includes('channel'),
        )
      ) {
        return Promise.reject(missingChannelError);
      }

      // Check if token is expired
      if (
        error?.result?.error?.code === 401 ||
        (error?.message &&
          (error.message.includes('invalid_token') ||
            error.message.includes('expired')))
      ) {
        return Promise.reject('token_expired');
      }
    }

    const response = await fetch(audio.summaryVideoUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch video file: ${response.status} ${response.statusText}`,
      );
    }
    const videoBlob = await response.blob();

    if (videoBlob.size === 0) {
      throw new Error('Video file is empty');
    }

    const metadata = {
      snippet: {
        title: audio.title,
        description: audio.scriptSummary,
        tags: ['wellnite'],
        categoryId: PEOPLE_BLOGS_CATEGORY,
      },
      status: {
        privacyStatus: 'public',
        selfDeclaredMadeForKids: audio.madeForKids,
      },
    };

    return new Promise((resolve, reject) => {
      const form = new FormData();
      const fileMetadata = new Blob([JSON.stringify(metadata)], {
        type: 'application/json',
      });
      form.append('metadata', fileMetadata, 'metadata.json');
      const fileName =
        audio.summaryVideoUrl.split('/').pop() || 'wellnite-audio-short.mp4';
      form.append('file', videoBlob, fileName);

      const xhr = new XMLHttpRequest();
      const uploadUrl = `https://www.googleapis.com/upload/youtube/v3/videos?uploadType=multipart&part=snippet,status`;

      xhr.open('POST', uploadUrl, true);
      xhr.setRequestHeader('Authorization', `Bearer ${authData.accessToken}`);

      // Handle progress
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100,
          );
          console.log(`Upload progress: ${percentComplete}%`);
        }
      };

      // Handle completion
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve({
              videoId: response.id,
              videoUrl: `https://www.youtube.com/watch?v=${response.id}`,
              title: response.snippet.title,
            });
          } catch (error) {
            console.error('Error parsing response:', error);
            reject('Failed to parse upload response');
          }
          return;
        }

        if (xhr.status === 401) {
          reject('token_expired');
          return;
        }

        console.error('Upload failed:', xhr.status, xhr.statusText);
        let errorResponse;

        try {
          errorResponse = JSON.parse(xhr.responseText);

          if (
            errorResponse.error?.message?.includes('channel') ||
            errorResponse.error?.errors?.some(
              (err: any) => err.reason === 'youtubeSignupRequired',
            )
          ) {
            reject({
              error: {
                message: 'User is not connected to a YouTube channel',
                errors: [{reason: 'youtubeSignupRequired'}],
              },
            });
            return;
          }
        } catch (e) {
          // Can't parse as JSON, use raw text
          errorResponse = xhr.responseText;
        }

        reject(errorResponse || `Upload failed with status: ${xhr.status}`);
      };

      xhr.onerror = () => {
        console.error('Network error during upload');
        reject('Network error during upload');
      };

      xhr.send(form);
    });
  } catch (error) {
    console.error('YouTube upload error:', error);
    return Promise.reject(error);
  }
};
